import { db } from './firebase';
import { collection, doc, getDoc, setDoc, updateDoc, query, where, getDocs, orderBy, limit, serverTimestamp, increment } from 'firebase/firestore';

const USER_PREFERENCES_COLLECTION = 'userPreferences';
const USER_INTERACTIONS_COLLECTION = 'userInteractions';

// Track user interactions with questions
export const trackInteraction = async (userId, questionId, interactionType, category) => {
  if (!userId) return;
  
  try {
    // Ensure category is not undefined
    const validCategory = category || 'uncategorized';
    
    // Track in interactions collection
    const interactionRef = doc(db, USER_INTERACTIONS_COLLECTION, `${userId}_${questionId}`);
    await setDoc(interactionRef, {
      userId,
      questionId,
      category: validCategory,
      interactionType,
      timestamp: serverTimestamp(),
      weight: getInteractionWeight(interactionType)
    }, { merge: true });
    
    // Update user preferences for category
    const userPrefRef = doc(db, USER_PREFERENCES_COLLECTION, userId);
    const userPrefDoc = await getDoc(userPrefRef);
    
    if (userPrefDoc.exists()) {
      const categoryKey = `categories.${validCategory}`;
      await updateDoc(userPrefRef, {
        [categoryKey]: increment(getInteractionWeight(interactionType)),
        lastUpdated: serverTimestamp()
      });
    } else {
      await setDoc(userPrefRef, {
        userId,
        categories: {
          [validCategory]: getInteractionWeight(interactionType)
        },
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error tracking interaction:', error);
  }
};

// Get interaction weight based on type
const getInteractionWeight = (interactionType) => {
  const weights = {
    view: 1,
    vote: 3,
    comment: 5,
    tomato: 2,
    clap: 2
  };
  return weights[interactionType] || 1;
};

// Get user's category preferences
export const getUserCategoryPreferences = async (userId) => {
  if (!userId) return {};
  
  try {
    const userPrefRef = doc(db, USER_PREFERENCES_COLLECTION, userId);
    const userPrefDoc = await getDoc(userPrefRef);
    
    if (userPrefDoc.exists()) {
      return userPrefDoc.data().categories || {};
    }
    return {};
  } catch (error) {
    console.error('Error getting user preferences:', error);
    return {};
  }
};

// Find users with similar voting patterns
export const findSimilarUsers = async (userId, limit = 10) => {
  if (!userId) return [];
  
  try {
    // Get current user's interactions
    const userInteractionsQuery = query(
      collection(db, USER_INTERACTIONS_COLLECTION),
      where('userId', '==', userId),
      where('interactionType', 'in', ['vote', 'comment', 'clap'])
    );
    const userInteractionsSnap = await getDocs(userInteractionsQuery);
    
    const userQuestions = new Set();
    userInteractionsSnap.forEach(doc => {
      userQuestions.add(doc.data().questionId);
    });
    
    if (userQuestions.size === 0) return [];
    
    // Find other users who interacted with same questions
    const similarityScores = new Map();
    
    for (const questionId of userQuestions) {
      const otherUsersQuery = query(
        collection(db, USER_INTERACTIONS_COLLECTION),
        where('questionId', '==', questionId),
        where('userId', '!=', userId)
      );
      const otherUsersSnap = await getDocs(otherUsersQuery);
      
      otherUsersSnap.forEach(doc => {
        const otherUserId = doc.data().userId;
        const currentScore = similarityScores.get(otherUserId) || 0;
        similarityScores.set(otherUserId, currentScore + doc.data().weight);
      });
    }
    
    // Sort by similarity score and return top users
    const sortedUsers = Array.from(similarityScores.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(entry => entry[0]);
    
    return sortedUsers;
  } catch (error) {
    console.error('Error finding similar users:', error);
    return [];
  }
};

// Get questions liked by similar users
export const getQuestionsFromSimilarUsers = async (userId, similarUsers, excludeQuestionIds = []) => {
  if (!similarUsers.length) return [];
  
  try {
    const recommendedQuestions = new Map();
    
    for (const similarUserId of similarUsers) {
      const interactionsQuery = query(
        collection(db, USER_INTERACTIONS_COLLECTION),
        where('userId', '==', similarUserId),
        where('interactionType', 'in', ['vote', 'comment', 'clap']),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      const interactionsSnap = await getDocs(interactionsQuery);
      
      interactionsSnap.forEach(doc => {
        const data = doc.data();
        if (!excludeQuestionIds.includes(data.questionId)) {
          const currentScore = recommendedQuestions.get(data.questionId) || 0;
          recommendedQuestions.set(data.questionId, currentScore + data.weight);
        }
      });
    }
    
    // Sort by score and return question IDs
    const sortedQuestions = Array.from(recommendedQuestions.entries())
      .sort((a, b) => b[1] - a[1])
      .map(entry => entry[0]);
    
    return sortedQuestions;
  } catch (error) {
    console.error('Error getting questions from similar users:', error);
    return [];
  }
};

// Calculate trending score
export const calculateTrendingScore = (question) => {
  const now = Date.now();
  
  // Handle different timestamp formats
  let createdAt = now;
  if (question.createdAt) {
    if (typeof question.createdAt.toMillis === 'function') {
      createdAt = question.createdAt.toMillis();
    } else if (question.createdAt.seconds) {
      createdAt = question.createdAt.seconds * 1000;
    } else if (typeof question.createdAt === 'number') {
      createdAt = question.createdAt;
    } else if (question.createdAt instanceof Date) {
      createdAt = question.createdAt.getTime();
    }
  }
  
  const ageInHours = (now - createdAt) / (1000 * 60 * 60);
  
  // Engagement score
  const engagementScore = 
    (question.voteCount || 0) * 3 +
    (question.commentCount || 0) * 5 +
    (question.viewCount || 0) * 1 +
    (question.tomatoCount || 0) * 2 +
    (question.clapCount || 0) * 2;
  
  // Time decay factor (newer posts get higher scores)
  const timeDecay = Math.pow(0.95, ageInHours / 24);
  
  return engagementScore * timeDecay;
};

// Main recommendation function
export const getRecommendedQuestions = async (userId, allQuestions, currentCategory = null) => {
  try {
    console.log('Getting recommendations for:', { userId, questionsCount: allQuestions?.length, currentCategory });
    
    // If no questions provided, return empty array
    if (!allQuestions || allQuestions.length === 0) {
      console.log('No questions provided to recommendation algorithm');
      return [];
    }
    
    // If no user, just return trending
    if (!userId) {
      console.log('No userId, returning trending questions');
      return allQuestions.sort((a, b) => calculateTrendingScore(b) - calculateTrendingScore(a));
    }
    
    // Get user's category preferences
    const categoryPrefs = await getUserCategoryPreferences(userId);
    
    // Find similar users
    const similarUsers = await findSimilarUsers(userId, 5);
    
    // Get questions user has already interacted with
    const userInteractionsQuery = query(
      collection(db, USER_INTERACTIONS_COLLECTION),
      where('userId', '==', userId)
    );
    const userInteractionsSnap = await getDocs(userInteractionsQuery);
    const interactedQuestionIds = new Set();
    userInteractionsSnap.forEach(doc => {
      interactedQuestionIds.add(doc.data().questionId);
    });
    
    // Get questions from similar users
    const similarUserQuestionIds = await getQuestionsFromSimilarUsers(
      userId, 
      similarUsers, 
      Array.from(interactedQuestionIds)
    );
    
    // Score each question
    const scoredQuestions = allQuestions.map(question => {
      let score = 0;
      
      // 40% - Category preference score
      if (categoryPrefs[question.category] && Object.keys(categoryPrefs).length > 0) {
        const categoryValues = Object.values(categoryPrefs);
        const maxCategoryScore = Math.max(...categoryValues);
        if (maxCategoryScore > 0) {
          const categoryScore = (categoryPrefs[question.category] / maxCategoryScore) * 0.4;
          score += categoryScore;
        }
      }
      
      // 30% - Trending/hot score
      const trendingScores = allQuestions.map(q => calculateTrendingScore(q));
      const maxTrendingScore = Math.max(...trendingScores);
      if (maxTrendingScore > 0) {
        const trendingScore = (calculateTrendingScore(question) / maxTrendingScore) * 0.3;
        score += trendingScore;
      }
      
      // 30% - Similar users score
      if (similarUserQuestionIds.includes(question.id)) {
        const position = similarUserQuestionIds.indexOf(question.id);
        const similarUserScore = ((similarUserQuestionIds.length - position) / similarUserQuestionIds.length) * 0.3;
        score += similarUserScore;
      }
      
      // Boost score if in current category
      if (currentCategory && question.category === currentCategory) {
        score *= 1.2;
      }
      
      // Deprioritize already seen questions
      if (interactedQuestionIds.has(question.id)) {
        score *= 0.5;
      }
      
      return { ...question, recommendationScore: score };
    });
    
    // 🔥 NUCLEAR FIX: Completely ignore recommendation scores, just random shuffle
    console.log('🎲 RECOMMENDATION OVERRIDE: Ignoring all scores, doing pure random shuffle');
    const shuffled = [...scoredQuestions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  } catch (error) {
    console.error('Error getting recommendations:', error);
    // Fallback to trending
    return allQuestions.sort((a, b) => calculateTrendingScore(b) - calculateTrendingScore(a));
  }
};